/**
 * มือถือเป็น "database" ของระบบ — เก็บ state ตัวละครและเป็นคนสั่งข้ามฉาก
 * (ARCHITECTURE.md §2.1) เซิร์ฟเวอร์ไม่เก็บพิกัดเลย
 *
 * เก็บลง localStorage เพื่อให้รีเฟรช/เน็ตหลุดแล้ว spawn กลับที่เดิมได้ (T7)
 */
import {
  AVATAR, FRAME_ORDER, FRAMES_PER_SHEET, GROUND_Y, type ProfileMsg, type SceneId,
  type StyleId,
} from "@py/protocol";

const KEY = "py.session.v1";

export type Session = {
  sessionId: string;
  playerId: string;
  nickname?: string;
  status: "queued" | "generating" | "ready" | "failed";
  /** สถานะการวาดด้วย AI — pending = ห้ามพาไปเล่น (จะเห็นภาพร่าง) */
  aiStatus?: string;
  urls?: { s1: string; s2: string; s3: string; s4?: string };
  createdAt: number;
  expiresAt: number;
  /** ตำแหน่งล่าสุดที่ NB รายงานกลับ — ใช้ตอน reconnect */
  scene: SceneId;
  x: number;
  y: number;
  score: number;
};

export function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    if (!s.playerId || !s.sessionId) return null;
    if (s.expiresAt && s.expiresAt < Date.now()) {
      localStorage.removeItem(KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function saveSession(s: Session): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // โหมดส่วนตัวของ Safari เขียนไม่ได้ — ยังเล่นได้ในหน้าเดียว จึงไม่ throw
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ไม่มีอะไรต้องทำ */
  }
}

/**
 * แปลง session → payload `profile` ที่จอต้องใช้ (MQTT_PROTOCOL.md §2.5)
 *
 * **มือถือเป็นคนประกาศ profile เอง** Cloudflare ไม่แตะ MQTT เลย
 * ถ้าไม่ประกาศ จอจะไม่รู้ URL ตัวละครแล้ววาดเป็นเงาดำแทน
 * คืน null เมื่อยังไม่มี URL (ตัวละครยังสร้างไม่เสร็จ)
 */
export function profileFromSession(s: Session): ProfileMsg | null {
  if (!s.urls) return null;
  const entry = (n: StyleId) => {
    const [w, h] = AVATAR[n];
    // s4 อาจยังไม่มี (session เก่าก่อนเพิ่มสไตล์ anime) — ใช้ s3 แทน จอไม่พังกลางงาน
    const url = s.urls![`s${n}` as "s1" | "s2" | "s3" | "s4"] ?? s.urls!.s3;
    return { url, w, h, frames: FRAMES_PER_SHEET };
  };
  return {
    p: s.playerId,
    ...(s.nickname ? { nick: s.nickname } : {}),
    styles: { "1": entry(1), "2": entry(2), "3": entry(3), "4": entry(4) },
    frameOrder: [...FRAME_ORDER],
    createdAt: s.createdAt,
    expiresAt: s.expiresAt,
  };
}

export function newSession(sessionId: string, playerId: string, nickname?: string): Session {
  const now = Date.now();
  return {
    sessionId,
    playerId,
    nickname,
    status: "queued",
    createdAt: now,
    expiresAt: now + 6 * 60 * 60 * 1000,
    scene: 0,
    x: 540,
    y: GROUND_Y,
    score: 0,
  };
}
